import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  GoogleMap,
  DirectionsRenderer,
  DirectionsService,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import LeftNav from "../../components/leftNav/LeftNav";
import TopNav from "../../components/topNav/TopNav";

type EstadoViatico = "Pendiente" | "Aprobado" | "Devuelto";

interface Viatico {
  id?: number;
  destino: string;
  fecha: string;
  kilometros: number;
  monto: number;
  tiempo: number;
  estado: EstadoViatico;
}

const libraries: "places"[] = ["places"];

const DetailViaticos: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"info" | "historial">("info");
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [viatico, setViatico] = useState<Viatico>({
    id: 1,
    destino: "Cartago, Costa Rica",
    fecha: "2025-10-05",
    kilometros: 0,
    monto: 0,
    tiempo: 0,
    estado: "Pendiente",
  });

  const GOOGLE = "AIzaSyCjJICPUL8O1rlFP_Ot0MPmtjJ4DwqD64A";

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE,
    libraries,
  });

  const [directionsResult, setDirectionsResult] = useState<any>(null);
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);
  const [shouldFetchDirections, setShouldFetchDirections] = useState(true);

  const origin = "San José, Costa Rica";
  const destination = viatico.destino;

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const calcularMonto = (kms: number, tiempo: number) => {
    let montoBase = kms * 200;
    let extras = 0;
    if (tiempo >= 3 && tiempo < 6) extras += 5000;
    if (tiempo >= 6 && tiempo < 10) extras += 10000;
    if (tiempo >= 10) extras += 15000;
    return montoBase + extras;
  };

  const handleTiempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const tiempo = Number(e.target.value);
    setViatico((prev) => ({
      ...prev,
      tiempo,
      monto: calcularMonto(prev.kilometros, tiempo),
    }));
  };

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        setViatico((prev: any) => ({
          ...prev,
          destino: place.formatted_address,
        }));
      } else if (place.name) {
        setViatico((prev: any) => ({
          ...prev,
          destino: place.name,
        }));
      }
    }
  };

  useEffect(() => {
    if (destination) {
      setShouldFetchDirections(true);
      setSelectedRoute(null);
    }
  }, [destination]);

  const handleSelectRoute = useCallback(
    (index: number) => {
      if (!directionsResult?.routes?.[index]) return;
      const selected = directionsResult.routes[index];
      const kms = (selected.legs[0].distance.value / 1000) * 2;

      setViatico((prev) => ({
        ...prev,
        kilometros: kms,
        monto: calcularMonto(kms, prev.tiempo),
      }));
      setSelectedRoute(index);
    },
    [directionsResult]
  );

  if (!isLoaded) return <p className="p-6">Cargando mapa...</p>;

  return (
    <div className="flex h-screen bg-gray-100">
      <LeftNav />
      <div className="flex flex-1 flex-col">
        <TopNav />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="p-6">
            <div className="mb-4 flex border-b">
              <button
                onClick={() => setActiveTab("info")}
                className={`px-4 py-2 ${
                  activeTab === "info"
                    ? "border-b-2 border-blue-600 font-semibold"
                    : "text-gray-500"
                }`}
              >
                Información
              </button>
              <button
                onClick={() => setActiveTab("historial")}
                className={`px-4 py-2 ${
                  activeTab === "historial"
                    ? "border-b-2 border-blue-600 font-semibold"
                    : "text-gray-500"
                }`}
              >
                Historial
              </button>
            </div>

            {activeTab === "info" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium">Destino</label>
                  <Autocomplete
                    onLoad={(ref) => (autocompleteRef.current = ref)}
                    onPlaceChanged={handlePlaceChanged}
                  >
                    <input
                      type="text"
                      value={viatico.destino}
                      onChange={(e) =>
                        setViatico({ ...viatico, destino: e.target.value })
                      }
                      placeholder="Escribe un destino..."
                      className="mt-1 w-full rounded border px-3 py-2"
                    />
                  </Autocomplete>
                </div>

                <div>
                  <label className="block text-sm font-medium">Fecha</label>
                  <input
                    type="date"
                    value={viatico.fecha}
                    onChange={(e) =>
                      setViatico({ ...viatico, fecha: e.target.value })
                    }
                    className="mt-1 w-full rounded border px-3 py-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium">
                    Tiempo estimado (horas)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={viatico.tiempo}
                    onChange={handleTiempoChange}
                    className="mt-1 w-full rounded border px-3 py-2"
                  />
                </div>

                <div className="h-64 w-full border rounded relative">
                  <GoogleMap
                    center={{ lat: 9.9281, lng: -84.0907 }}
                    zoom={10}
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                  >
                    {shouldFetchDirections && (
                      <DirectionsService
                        options={{
                          origin,
                          destination,
                          travelMode: google.maps.TravelMode.DRIVING,
                          provideRouteAlternatives: true,
                        }}
                        callback={(res) => {
                          if (res && res.routes && res.request) {
                            if (
                              !directionsResult ||
                              JSON.stringify(res.routes) !==
                                JSON.stringify(directionsResult.routes)
                            ) {
                              setDirectionsResult(res);
                            }
                            setShouldFetchDirections(false);
                          }
                        }}
                      />
                    )}

                    {/* Renderiza la ruta seleccionada */}
                    {directionsResult &&
                      selectedRoute !== null &&
                      directionsResult.routes[selectedRoute] && (
                        <DirectionsRenderer
                          directions={{
                            ...directionsResult,
                            routes: [directionsResult.routes[selectedRoute]],
                          }}
                        />
                      )}
                  </GoogleMap>

                  {directionsResult?.routes?.length > 0 && (
                    <div className="mt-2">
                      <p className="font-medium">Opciones de ruta:</p>
                      {directionsResult.routes.map((r: any, i: number) => (
                        <button
                          key={i}
                          onClick={() => handleSelectRoute(i)}
                          className={`mr-2 mb-2 rounded px-3 py-1 ${
                            selectedRoute === i
                              ? "bg-blue-600 text-white"
                              : "bg-gray-200"
                          }`}
                        >
                          Ruta {i + 1} - {r.legs[0].distance.text} (
                          {r.legs[0].duration.text})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <br />
                <br />
                <br />
                <div className="mt-4 rounded border p-4 bg-gray-50">
                  <p>
                    <strong>Kilómetros (ida y vuelta):</strong>{" "}
                    {viatico.kilometros.toFixed(2)} km
                  </p>
                  <p>
                    <strong>Monto total:</strong> ₡
                    {viatico.monto.toLocaleString()}
                  </p>
                </div>
              </div>
            )}

            {activeTab === "historial" && (
              <div>
                {" "}
                <div className="flex justify-between mb-4">
                  {" "}
                  <input
                    type="date"
                    className="border px-3 py-2 rounded w-1/3"
                  />{" "}
                  <button
                    onClick={() => setShowCommentModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded"
                  >
                    {" "}
                    Agregar Comentario{" "}
                  </button>{" "}
                </div>{" "}
                <table className="w-full border text-left">
                  {" "}
                  <thead className="bg-gray-100">
                    {" "}
                    <tr>
                      {" "}
                      <th className="p-2 border">Fecha</th>{" "}
                      <th className="p-2 border">Acción</th>{" "}
                      <th className="p-2 border">Usuario</th>{" "}
                      <th className="p-2 border">Comentario</th>{" "}
                    </tr>{" "}
                  </thead>{" "}
                  <tbody>
                    {" "}
                    <tr>
                      {" "}
                      <td className="p-2 border">2025-09-20</td>{" "}
                      <td className="p-2 border">Revision</td>{" "}
                      <td className="p-2 border">Eduardo</td>{" "}
                      <td className="p-2 border">
                        {" "}
                        Aprovo los viaticos solicitados{" "}
                      </td>{" "}
                    </tr>{" "}
                    <tr>
                      {" "}
                      <td className="p-2 border">2025-09-21</td>{" "}
                      <td className="p-2 border">Solicitud</td>{" "}
                      <td className="p-2 border">Ana</td>{" "}
                      <td className="p-2 border">
                        {" "}
                        Solicito viatico para viaje a Cartago{" "}
                      </td>{" "}
                    </tr>{" "}
                  </tbody>{" "}
                </table>{" "}
              </div>
            )}
          </div>
        </main>
      </div>
      {showCommentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white p-6 rounded shadow w-96">
            <h3 className="text-lg font-bold mb-2">Agregar Comentario</h3>
            <textarea
              className="w-full border rounded p-2 mb-4"
              placeholder="Escribe un comentario"
            ></textarea>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowCommentModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancelar
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded">
                Guardar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailViaticos;
